import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, PortalInjector } from '@angular/cdk/portal';

import { ModalOptions } from '../models/modal-options';
import { MCRAI_MODAL_DATA } from '../models/modal-data';

const DEFAULT_MODAL_OPTIONS = new ModalOptions({
  width: '640px',
  height: '640px'
});

@Injectable()
export class McRAIModal {
  private _overlayRef: OverlayRef;
  private _overlayRefs: OverlayRef[] = [];

  constructor(
    private _overlay: Overlay,
    private _injector: Injector,
  ) { }

  private _getConfig(options: ModalOptions): OverlayConfig {
    const config = new OverlayConfig();

    config.positionStrategy = this._overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    config.width  = options.width;
    config.height = options.height;

    config.hasBackdrop = true;

    return config;
  }

  private _getInjector(data: Object): PortalInjector {
    const customTokens = new WeakMap();

    customTokens.set(MCRAI_MODAL_DATA, data);

    return new PortalInjector(this._injector, customTokens);
  }

  private _getComponentPortal<T>(component: ComponentType<T>, data: Object): ComponentPortal<T> {
    return new ComponentPortal<T>(component, undefined, this._getInjector(data));
  }

  public open<T>(component: ComponentType<T>, options: ModalOptions = DEFAULT_MODAL_OPTIONS): void {
    const config = this._getConfig(options);
    const portal = this._getComponentPortal(component, options.data);

    this._overlayRef = this._overlay.create(config);

    this._overlayRef
      .backdropClick()
      .subscribe(() => this.close());

    this._overlayRef.attach(portal);
    this._overlayRefs.push(this._overlayRef);
  }

  public close(): void {
    const ref = this._overlayRefs.pop();
    if (ref) {
      ref.dispose();
    }
  }

  public resize(width: string, height: string): void {
    this._overlayRef.updateSize({
      width: width,
      height: height
    });
  }
}
